import {Inject, Injectable} from '@angular/core';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import {CommentsEnum} from '../enums/PluralEnums';
import {PluralService} from './plural.service';
import {WPMedia} from '../types/WPMedia';
import {WPTag} from '../types/WPTag';
import {MediaSizeEnum} from '../enums/WPEnums';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {ApiHelper} from './api.helper';
import {WPMediaFlat} from '../types/WPMediaFlat';
import {ToolsService} from './tools.service';
import {WPCustomFieldsTaxonomyItem} from '../types/WPCustomFieldsTaxonomyItem';
import {WPPostBlog} from '../types/WPPostBlog';
import {WPPostGuide} from '../types/WPPostGuide';
import {WPPostNative} from '../types/WPPostNative';
import {WPPageCustom} from '../types/WPPageCustom';
import {WPPostLandingPage} from '../types/WPPostLandingPage';
import {WPPostLandingPageCampaign} from '../types/WPPostLandingPageCampaign';
import {DLPageTypeEnum} from '../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../types/DataLayerParamsPageViewGA4';
import {DataLayerParamsClickGA4} from '../types/DataLayerParamsClickGA4';
import {AppConfigService} from './app-config.service';
import {DOCUMENT} from '@angular/common';
import {CodeSnippetsService} from './code-snippets.service';
import {SchemaCodeSnippetIdEnum} from '../enums/SchemaEnums';
import {WPUserArray} from '../types/WPUserArray';
import {WPPostCareServiceTypePage} from '../modules/search/modules/search-content-pages-v2/typings/WPPostCareServiceTypePage';
import {WPPostSymptomPage} from '../modules/search/modules/search-content-pages-v2/typings/WPPostSymptomPage';

interface TaxonomyItemsCount {
  taxonomyItem: WPCustomFieldsTaxonomyItem;
  count: number;
}

@Injectable()
export class WpService {
  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private pluralService: PluralService,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private headerScriptService: CodeSnippetsService,
    @Inject(DOCUMENT) private doc: any,
  ) {
    dayjs.locale('sv');
  }

  public GetTaxonomyItems(taxonomyType: string, slugs?: string[]): Observable<WPTag[]> {
    const urlParts: string[] = [];
    urlParts.push(`page=1`);
    urlParts.push(`per_page=100`);
    urlParts.push(`order=desc`);
    urlParts.push(`orderby=count`);
    urlParts.push(`_fields=${encodeURIComponent('id,count,description,name,slug,acf')}`);
    if (!this.tools.isNil(slugs) && slugs.length > 0) {
      urlParts.push(`slug=${slugs.join(',')}`);
    }
    const endpoint: string = this.appConfigService.appConfig.wordpressApiUrl + `${taxonomyType}?${urlParts.join('&')}`;

    return this.http.get<WPTag[]>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetTaxonomyItem(taxonomyType: string, id: number): Observable<WPTag> {
    const endpoint: string =
      this.appConfigService.appConfig.wordpressApiUrl +
      `${taxonomyType}/${id}?_fields=${encodeURIComponent('id,count,description,name,slug,acf')}`;

    return this.http.get<WPTag>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetTaxonomyItemBySlug(taxonomyType: string, slug: string): Observable<WPTag[]> {
    return this.http
      .get<
        WPTag[]
      >(this.appConfigService.appConfig.wordpressApiUrl + `${taxonomyType}?slug=${slug}&_fields=${encodeURIComponent('id,count,description,name,slug,acf')}`)
      .pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public getImageUrl(mediaItem: WPMedia): string {
    const url: string = mediaItem.media_details.sizes[MediaSizeEnum.LARGE].source_url;
    return this.removeWpUrlArtifacts(url);
  }

  public getImageFlatUrl(mediaItem: WPMediaFlat): string {
    if (!mediaItem || !mediaItem.sizes) {
      return null;
    }
    let url: string = mediaItem.sizes[MediaSizeEnum.BIGGEST];
    return this.removeWpUrlArtifacts(url);
  }

  public getFormattedDate(date: string, type?: string): string {
    let formatedDate: string = '';
    if (type == null || type === 'simple') {
      formatedDate = dayjs(date).format('YYYY-MM-DD');
    }
    if (type === 'full') {
      formatedDate = `${dayjs(date).format('D MMMM YYYY')} kl ${dayjs(date).format('HH:mm')}`;
    }
    return formatedDate;
  }

  public getCommentsHeader(itemsCount: number): string {
    return this.pluralService.getPluralString(CommentsEnum, itemsCount, true);
  }

  public getTagById(id: number, tags: WPTag[]): WPTag {
    return tags.find((item) => item.id === id);
  }

  public createDataLayerGa4CustomParams(
    post:
      | WPPostBlog
      | WPPostGuide
      | WPPostNative
      | WPPageCustom
      | WPPostLandingPage
      | WPPostLandingPageCampaign
      | WPPostCareServiceTypePage
      | WPPostSymptomPage,
    type: DLPageTypeEnum,
    title: string,
  ): DataLayerParamsPageViewGA4 {
    const customParams: DataLayerParamsPageViewGA4 = {
      pageType: type,
      pageTitle: title,
    };
    if (!this.tools.isNil(post.acf.mainCareProviderUnitType) && post.acf.mainCareProviderUnitType) {
      customParams.inferredCategoryTypeId = parseInt(post.acf.mainCareProviderUnitType.description, 10);
      customParams.mainCareProviderTypeId = parseInt(post.acf.mainCareProviderUnitType.description, 10);
      const splittedName: string[] = post.acf.mainCareProviderUnitType.name.split(':');
      customParams.inferredCareProviderTypeAreaId = parseInt(splittedName[1], 10);
    }
    if (!this.tools.isNil(post.acf.mainCareServiceType) && post.acf.mainCareServiceType) {
      customParams.careServiceTypeId = parseInt(post.acf.mainCareServiceType.description, 10);
    }
    if (!this.tools.isNil(post.acf.mainSymptom) && post.acf.mainSymptom) {
      customParams.symptomId = parseInt(post.acf.mainSymptom.description, 10);
    }
    return customParams;
  }

  public createDataLayerGa4ClickCustomParams(
    post:
      | WPPostBlog
      | WPPostGuide
      | WPPostNative
      | WPPageCustom
      | WPPostLandingPage
      | WPPostLandingPageCampaign
      | WPPostCareServiceTypePage
      | WPPostSymptomPage,
    callToAction: string,
    type: DLPageTypeEnum,
    title: string,
  ): DataLayerParamsClickGA4 {
    const customGa4Params: DataLayerParamsPageViewGA4 = this.createDataLayerGa4CustomParams(post, type, title);
    const customParams: DataLayerParamsClickGA4 = {
      callToAction: callToAction,
      inferredCareProviderTypeAreaId: customGa4Params.inferredCareProviderTypeAreaId,
      inferredCategoryTypeId: customGa4Params.inferredCategoryTypeId,
      mainCareProviderTypeId: customGa4Params.mainCareProviderTypeId,
      careServiceId: customGa4Params.careServiceId,
      careServiceTypeId: customGa4Params.careServiceTypeId,
      symptomId: customGa4Params.symptomId,
      cpruId: customGa4Params.cpruId,
      treatmentId: customGa4Params.treatmentId,
      pageType: type,
    };
    return customParams;
  }

  public createDataLayerGa4CustomParamsForItemsList(
    posts: WPPostBlog[] | WPPostGuide[] | WPPostNative[],
    type: DLPageTypeEnum,
    title: string,
  ): DataLayerParamsPageViewGA4 {
    const customParams: DataLayerParamsPageViewGA4 = {
      pageType: type,
      pageTitle: title,
    };

    const maxItems = 5;
    const numberOfItemsToCalculate: number = posts ? (posts.length > maxItems ? maxItems : posts.length) : 0;
    const mainUnitTypes: WPCustomFieldsTaxonomyItem[] = [];
    for (var i = 0; i < numberOfItemsToCalculate; i++) {
      if (!this.tools.isNil(posts[i].acf.mainCareProviderUnitType)) {
        mainUnitTypes.push(posts[i].acf.mainCareProviderUnitType);
      }
    }
    const commonType: WPCustomFieldsTaxonomyItem = this.getMostCommonCpruType(mainUnitTypes);

    if (!this.tools.isNil(commonType) && !!commonType !== false) {
      customParams.inferredCategoryTypeId = parseInt(commonType.description, 10);
      const splittedName: string[] = commonType.name.split(':');
      customParams.inferredCareProviderTypeAreaId = parseInt(splittedName.pop(), 10);
    }
    return customParams;
  }

  public getMostCommonCpruType(taxonomyItems: WPCustomFieldsTaxonomyItem[]): WPCustomFieldsTaxonomyItem {
    if (this.tools.isNil(taxonomyItems) || taxonomyItems.length === 0) {
      return null;
    }
    const groupedTaxonomyItems = this.tools.groupBy(taxonomyItems, 'term_id');
    const countedTaxonomyItems: TaxonomyItemsCount[] = [];
    Object.keys(groupedTaxonomyItems).forEach((key) => {
      countedTaxonomyItems.push({
        taxonomyItem: groupedTaxonomyItems[key][0],
        count: groupedTaxonomyItems[key].length,
      });
    });
    countedTaxonomyItems.sort(this.tools.sortBy('count'));
    return countedTaxonomyItems.pop().taxonomyItem;
  }

  public setDefaultWebPImageType(url: string): string {
    if (url.slice(-5) === '.webp') {
      return url;
    } else {
      return `${url}.webp`;
    }
  }

  public removeWpUrlArtifacts(url: string): string {
    const regex: RegExp = new RegExp(`(http|https):\/\/([^\/'"]*)\/cms-data\/`, 'g');
    return url.replace(regex, `${this.appConfigService.appConfig.wpBackend}`);
  }

  public addSchema(
    blog: WPPostBlog,
    guide: WPPostGuide,
    csTypePage: WPPostCareServiceTypePage,
    symptomPage: WPPostSymptomPage,
    native: WPPostNative,
  ): void {
    const schema = this.createArticleJsonSchema(blog, guide, csTypePage, symptomPage, native);
    this.headerScriptService.addScriptToHead(schema);
  }

  public removeSchema(): void {
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.ARTICLE}`);
  }

  private createArticleJsonSchema(
    blog: WPPostBlog,
    guide: WPPostGuide,
    csTypePage: WPPostCareServiceTypePage,
    symptomPage: WPPostSymptomPage,
    native: WPPostNative,
  ): any {
    let headline: string = '';
    const author = [];

    let authorObject: WPUserArray[] = null;
    let authorName: string = null;
    let articleBody: string = null;
    let datePublished: string = null;
    let dateModified: string = null;
    let image = [];
    if (blog) {
      headline = blog.title.rendered;
      authorObject = blog.acf.author;
      authorName = blog.acf.authorName;
      articleBody = this.tools.removeHtmlTags(blog.excerpt.rendered + blog.content.rendered);
      datePublished = blog.date;
      dateModified = blog.modified;
      if (blog.acf.mainImage) {
        image = [this.setDefaultWebPImageType(this.getImageFlatUrl(blog.acf.mainImage))];
      }
    } else if (native) {
      headline = native.title.rendered;
      articleBody = this.tools.removeHtmlTags(native.excerpt.rendered + native.content.rendered);
      datePublished = native.date;
      dateModified = native.modified;
    } else if (guide) {
      headline = guide.title.rendered;
      authorObject = guide.acf.author;
      authorName = guide.acf.authorName;
      articleBody = this.tools.removeHtmlTags(guide.acf.mainIntroduction + guide.content.rendered);
      datePublished = guide.date;
      dateModified = guide.modified;
      if (guide.acf.mainImage) {
        image = [this.setDefaultWebPImageType(this.getImageFlatUrl(guide.acf.mainImage))];
      }
    } else if (csTypePage) {
      headline = csTypePage.title.rendered;
      authorObject = csTypePage.acf.author;
      authorName = csTypePage.acf.authorName;
      datePublished = csTypePage.date;
      dateModified = csTypePage.modified;
      let contentCombined: string = '';
      if (csTypePage.acf?.contentSections) {
        csTypePage.acf.contentSections.forEach((section) => {
          contentCombined += section.title + ' ' + section.content + ' ';
        });
        articleBody = this.tools.removeHtmlTags(contentCombined);
      }
      image = [
        this.setDefaultWebPImageType(this.getImageFlatUrl(csTypePage.acf.backgroundImageDesktop)),
        this.setDefaultWebPImageType(this.getImageFlatUrl(csTypePage.acf.backgroundImageTablet)),
        this.setDefaultWebPImageType(this.getImageFlatUrl(csTypePage.acf.backgroundImageMobile)),
      ];
    } else if (symptomPage) {
      headline = symptomPage.title.rendered;
      authorObject = symptomPage.acf.author;
      authorName = symptomPage.acf.authorName;
      datePublished = symptomPage.date;
      dateModified = symptomPage.modified;
      let contentCombined: string = '';
      if (symptomPage.acf?.contentSections) {
        symptomPage.acf.contentSections.forEach((section) => {
          contentCombined += section.title + ' ' + section.content + ' ';
        });
        articleBody = this.tools.removeHtmlTags(contentCombined);
      }
      image = [
        this.setDefaultWebPImageType(this.getImageFlatUrl(symptomPage.acf.backgroundImageDesktop)),
        this.setDefaultWebPImageType(this.getImageFlatUrl(symptomPage.acf.backgroundImageTablet)),
        this.setDefaultWebPImageType(this.getImageFlatUrl(symptomPage.acf.backgroundImageMobile)),
      ];
    }
    if (authorObject && authorObject.length > 0) {
      authorObject.forEach((authorItem) => {
        author.push({
          '@type': 'Person',
          name: authorItem.display_name,
        });
      });
    }
    if (authorName && authorName !== '') {
      author.push({
        '@type': 'Person',
        name: authorName,
      });
    }

    const jsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: headline,
      author: author,
      articleBody: articleBody,
      datePublished: datePublished,
      dateModified: dateModified,
      image: image,
    };

    const schemaScript = this.doc.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = SchemaCodeSnippetIdEnum.ARTICLE;
    schemaScript.innerHTML = JSON.stringify(jsonSchema);
    return schemaScript;
  }
}
